import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { recursiveFind } from '../../../utilities/treeHelpers';
import { fetchEntityTree } from '../../entity/entitySlice';
import { changeEntityParent } from '../../entity/entityApi';

const useParentAssignment = (orgId, entityTree) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showAssignParentModal, setShowAssignParentModal] = useState(false);
  const [selectedEntitiesForParent, setSelectedEntitiesForParent] = useState([]);
  const [showParentSelector, setShowParentSelector] = useState(false);
  const [parentAssignments, setParentAssignments] = useState({});
  const [tempParentAssignments, setTempParentAssignments] = useState({});

  const findEntityByName = (entityName) => {
    if (!entityTree || !entityName) return null;
    return recursiveFind(entityTree, (node) => node.divName === entityName);
  };

  const getEntityParentInfo = (entityName) => {
    if (tempParentAssignments[entityName]) {
      return tempParentAssignments[entityName].parentName;
    }
    if (parentAssignments[entityName]) {
      return parentAssignments[entityName].parentName;
    }
    const entity = findEntityByName(entityName);
    if (!entity || !entity.divParentId) return null;
    const parentEntity = recursiveFind(entityTree, (node) => node.divId === entity.divParentId);
    return parentEntity ? parentEntity.divName : null;
  };

  const handleOpenAssignParent = () => setShowAssignParentModal(true);

  const handleCloseAssignParent = () => {
    setShowAssignParentModal(false);
    setSelectedEntitiesForParent([]);
    setTempParentAssignments({});
  };

  const handleDoneAssignParent = async () => {
    if (Object.keys(tempParentAssignments).length === 0) {
      setShowAssignParentModal(false);
      return;
    }

    try {
      const updatePromises = [];
      const newEntityNames = [];

      for (const [entityName, assignment] of Object.entries(tempParentAssignments)) {
        const entity = findEntityByName(entityName);
        if (entity) {
          updatePromises.push(changeEntityParent(orgId, entity.divId, { parentId: assignment.parentId }));
        } else {
          newEntityNames.push(entityName);
        }
      }

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        await dispatch(fetchEntityTree({ orgId })).unwrap();
      }

      let successMessage = t('Parent assigned successfully');
      if (updatePromises.length > 0 && newEntityNames.length > 0) {
        successMessage = t('Parent assigned. Existing entities updated, new entities will be created on save.');
      } else if (newEntityNames.length > 0) {
        successMessage = t('Parent assigned. New entities will be created on save.');
      }

      toast.success(successMessage);
      setParentAssignments({ ...parentAssignments, ...tempParentAssignments });
      setShowAssignParentModal(false);
      setSelectedEntitiesForParent([]);
      setTempParentAssignments({});
    } catch (error) {
      toast.error(error.message || t('Failed to assign parent'));
    }
  };

  const handleOpenParentSelector = () => setShowParentSelector(true);
  const handleCloseParentSelector = () => setShowParentSelector(false);

  const handleSelectParent = useCallback(
    (parentEntity) => {
      const newAssignments = {};
      selectedEntitiesForParent.forEach((entityName) => {
        newAssignments[entityName] = {
          parentId: parentEntity.divId,
          parentName: parentEntity.divName
        };
      });
      setTempParentAssignments({ ...tempParentAssignments, ...newAssignments });
      setShowParentSelector(false);
    },
    [tempParentAssignments, selectedEntitiesForParent]
  );

  const handleToggleEntityForParent = (entityName) => {
    setSelectedEntitiesForParent((prev) =>
      prev.includes(entityName) ? prev.filter((name) => name !== entityName) : [...prev, entityName]
    );
  };

  return {
    parentAssignments,
    tempParentAssignments,
    showAssignParentModal,
    selectedEntitiesForParent,
    showParentSelector,
    getEntityParentInfo,
    handleOpenAssignParent,
    handleCloseAssignParent,
    handleDoneAssignParent,
    handleOpenParentSelector,
    handleCloseParentSelector,
    handleSelectParent,
    handleToggleEntityForParent
  };
};

export default useParentAssignment;
